import { Address } from "./address";
import { Affiliation } from "./affiliation";
import { AppEvent } from "./app-event";
import { LinkedInStatus, ValidityStatus } from "./enums";
import { Initiative } from "./initiative";
import { Interaction } from "./interaction";
import { Organisation } from "./organisation";
import { PhoneNumber } from "./phone-number";
import { Project } from "./project";
import { StakeholderGroup } from "./stakeholder-group";
import { SupplierDetails } from "./supplier-details";
import { TopicOfInterest } from "./topic-of-interest";

export class Contact {
  public id!: number;
  public firstName: string = '';
  public lastName: string = '';
  public affiliations: Affiliation[] = [];
  // public organisation: Organisation | null = null;
  public position: string | null = null;
  public email1: string | null = null;
  public email2: string | null = null;
  public phoneNumber: PhoneNumber | null = null;
  // public optOut: boolean = false;
  // public optOutDate: string | null = null;
  public additionalInfo: string | null = null;
  public postalAddress: Address | null = null;
  public linkedInProfile: string | null = null;
  public linkedInStatus: LinkedInStatus | null = LinkedInStatus.NOT_CONNECTED;
  public archived: boolean = false;

  public topics: TopicOfInterest[] = [];
  public stakeholderGroups: StakeholderGroup[] = []
  public interactions: Interaction[] = [];
  public projects: Project[] = [];
  public initiatives: Initiative[] = [];

  public events: AppEvent[] = [];

  public scientificAdvisoryBoard: boolean = false;
  public boardOfDirectors: boolean = false;
  public generalAssembly: boolean = false;
  public executiveCommittee: boolean = false;
  public nominationCommittee: boolean = false;
  public ethicsCommittee: boolean = false;

  public communicationType1OptIn: boolean = true;
  public communicationType2OptIn: boolean = true;
  public communicationType3OptIn: boolean = true;
  public communicationType4OptIn: boolean = true;
  public communicationType5OptIn: boolean = true;

  public supplierDetails: SupplierDetails | null = null;

  lastModifiedBy?: string | null;
  lastModifiedOn?: string | null;

  constructor(init?: Partial<Contact>) {
    Object.assign(this, init);
  }

  validityStatus(): ValidityStatus {
    const hasName = !!this.firstName && !!this.lastName;
    const hasEmail = !!this.email1 || !!this.email2;
    const hasStakeholderOrSupplier =
      (this.stakeholderGroups?.length ?? 0) > 0 || !!this.supplierDetails;
    const hasTopicsOfInterest = ((this.topics?.length ?? 0) > 0)
    const hasAffiliation = ((this.affiliations?.length ?? 0) > 0)

    if (!(hasName && hasEmail && hasStakeholderOrSupplier)) {
      return ValidityStatus.INVALID;
    } else if (!this.supplierDetails && !(hasTopicsOfInterest && hasAffiliation)) {
      // A supplier may have no "topics of interest' is no longer important
      // A supplier may not have an affiliation
      return ValidityStatus.INCOMPLETE;
    } else {
      return ValidityStatus.VALID;
    }
  }

  get emails(): string[] {
    return [this.email1, this.email2]
      .map(e => typeof e === 'string' ? e.trim() : '')
      .filter(e => e.length > 0);
  }

  get organisations(): string[] {
    const affiliations = this.affiliations ?? [];

    const organisations = Array.from(
      new Set(
        affiliations
          .filter(a => a.organisation)
          .map(a => {
            const org = a.organisation!;
            const name = org.acronym?.trim();
            return name ? (org.archived ? `${name} (Archived)` : name) : null;
          })
          .filter((n): n is string => !!n && n.length > 0)
      )
    );

    return organisations;
  }

  get countryCodes(): string[] {
    const contactCountry = this.postalAddress?.countryIsoCode;
    if (contactCountry && contactCountry.trim().length > 0) {
      return [contactCountry];
    }
    const affiliations = this.affiliations ?? [];
    const orgCountries = Array.from(
      new Set(
        affiliations
          .filter(a => a.organisation)
          .flatMap(a => a.organisation!.countries ?? [])
          .filter(c => c && c.trim().length > 0)
      )
    );
    return orgCountries;
  }

  get contactTypes(): string[] {
    const contactTypes: string[] = [];
    const affiliations = this.affiliations ?? [];
    if (affiliations.some(a => a.activePrimaryContact)) {
      contactTypes.push('Primary');
    }
    if (affiliations.some(a => a.activeInvoicingContact)) {
      contactTypes.push('Invoicing');
    }
    if (affiliations.some(a => a.activeContractContact)) {
      contactTypes.push('Contract');
    }
    if (this.boardOfDirectors) {
      contactTypes.push('BOD');
    }
    if (this.scientificAdvisoryBoard) {
      contactTypes.push('SAB');
    }
    if (this.generalAssembly) {
      contactTypes.push('GA');
    }
    if (this.executiveCommittee) {
      contactTypes.push('Executive');
    }
    if (this.nominationCommittee) {
      contactTypes.push('Nomination');
    }
    if (this.ethicsCommittee) {
      contactTypes.push('Ethics');
    }
    return contactTypes;
  }
}
